import styled from 'styled-components'
import UserCard from '../components/UserCard'
import { useMemo, useState } from 'react'
import { fetcher } from '../utils/fetcher'
import { Title } from 'react-head'
import { Button } from '@bm-js/atlascloud-ui'
import Loader from '../components/Loader'
import { breakPointsCss, colors } from '../utils/config'
import UserForm, { UserInput } from '../components/UserForm'
import DeleteConfirmation from '../components/DeleteConfirmation'
import { UserBM } from '@bm-js/atlascloud-shared'
import { usePopup, useToast, useToken, useUser } from '../utils/hooks'
import { useApi } from '../utils/useApi'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledUsers = styled.div`
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
  }
`

const Users = () => {
  const toast = useToast()
  const token = useToken()
  const user = useUser()
  const { tr } = useTr()
  const { closePopup, setPopup } = usePopup()

  const { data: users = [], refetch } = useApi<UserBM[]>('customer/users')
  const [loading, setLoading] = useState(false)

  const deleteUserInit = (user: UserBM) => {
    setPopup(
      <DeleteConfirmation
        item={user}
        deleteKey={'fullName'}
        name={tr(TrKey.usersTheUser)}
        handleCancel={closePopup}
        handleDelete={deleteUser}
      />
    )
  }

  const deleteUser = async (user: UserBM, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `customer/users/${user._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      cb()
      toast.error(tr(TrKey.usersFailedToDelete))
      setLoading(false)
      return
    }
    cb()
    closePopup()
    toast.success(tr(TrKey.usersUserDeleted))
    await refetch()
    setLoading(false)
  }

  const createUserInit = () => {
    setPopup(<UserForm handleSubmit={createUser} closePopup={closePopup} />)
  }

  const createUser = async (
    formData: UserInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: 'customer/users',
      method: 'POST',
      body: { ...formData, customerId: user?.customerId },
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.usersFailedToCreate))
      errCb()
      return
    }
    toast.success(tr(TrKey.usersUserCreated))
    cb()
    await refetch()
    setLoading(false)
    closePopup()
  }

  const editUserInit = (user: UserInput) => {
    setPopup(
      <UserForm handleSubmit={editUser} closePopup={closePopup} data={user} />
    )
  }
  const editUser = async (
    formData: UserInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: `customer/users/${formData._id}`,
      method: 'POST',
      body: formData,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.usersFailedToUpdate))
      errCb()
      return
    }
    toast.success(tr(TrKey.usersUserUpdated))
    cb()
    await refetch()
    setLoading(false)
    closePopup()
  }

  const title = useMemo(() => {
    return tr(TrKey.usersRouteTitle) + ' | Atlascloud'
  }, [tr])

  if (loading)
    return (
      <StyledUsers>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledUsers>
    )
  return (
    <StyledUsers className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb ac mbottom1 flex'>
        <h1 className='m0 l primblue'>{tr(TrKey.usersRouteTitle)}</h1>
        <Button onClick={createUserInit}>{tr(TrKey.usersCreateUser)}</Button>
      </div>
      <div className='users'>
        {users &&
          users.map((user) => (
            <UserCard
              deleteUser={() => deleteUserInit(user)}
              editUserInit={() => editUserInit(user)}
              user={user}
              key={user._id}
            />
          ))}
      </div>
    </StyledUsers>
  )
}

export default Users
